/**
 * AccountHistoryPanel — read-only display of an account's performance and
 * lockout history, sourced from:
 *   /api/v1/accounts/{account_id}/performance-history
 *   /api/v1/accounts/{account_id}/lockout-history
 *
 * Hard rules:
 *   - Read-only. No mutation, no broker credentials.
 *   - Never display raw JSON in main UI. Raw payloads only inside the
 *     collapsed Diagnostics block.
 *   - Never display raw ISO timestamps in main UI — formatted to ET.
 *   - Empty perf points => explicit "No realized P/L history has been
 *     recorded yet." Missing is NOT treated as zero.
 *   - HTTP failure / 404 => "Account history is not connected yet."
 *   - LIMITED badge when endpoint is live but produced no data points yet.
 */
import React, { useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Database,
  Lock,
  Loader2,
  ShieldAlert,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import {
  fmtCurrency,
  fmtEtTime,
  fmtRelative,
  humanizeLockoutReason,
  lockoutSeverity,
  severityToClass,
  severityToLabel,
  titleize,
} from './formatters';
import {
  LockoutEvent,
  useAccountHistory,
} from '../../hooks/useAccountHistory';

interface QualityBadgeProps {
  quality: 'LIVE' | 'LIMITED' | 'UNAVAILABLE';
  testid?: string;
}

function QualityBadge({ quality, testid }: QualityBadgeProps) {
  const klass =
    quality === 'LIVE'
      ? 'text-emerald-600 bg-emerald-500/10 border-emerald-500/40'
      : quality === 'LIMITED'
      ? 'text-amber-600 bg-amber-500/10 border-amber-500/40'
      : 'text-rose-600 bg-rose-500/10 border-rose-500/40';
  return (
    <span
      data-testid={testid}
      className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-md border ${klass}`}
    >
      {quality}
    </span>
  );
}

interface PerfSubsectionProps {
  bundle: ReturnType<typeof useAccountHistory>['bundle'];
}

function PerformanceSubsection({ bundle }: PerfSubsectionProps) {
  const perf = bundle?.performance ?? null;
  const quality = bundle?.performanceQuality ?? 'UNAVAILABLE';

  if (quality === 'UNAVAILABLE') {
    return (
      <div
        data-testid="account-history-perf-unavailable"
        className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 flex items-center gap-3"
      >
        <AlertTriangle size={14} className="text-amber-600 shrink-0" />
        <div className="text-[11px] font-bold text-amber-700">
          Account history is not connected yet.
        </div>
      </div>
    );
  }

  const summary = perf?.summary ?? {
    points_count: 0,
    first_timestamp: null,
    last_timestamp: null,
    realized_pnl_total: null,
    unrealized_pnl_latest: null,
  };
  const isEmpty = !perf || (perf.points?.length ?? 0) === 0;

  return (
    <div
      data-testid="account-history-perf"
      className="rounded-xl border border-[var(--card-border)] bg-[var(--input-bg)]/40 p-4 space-y-3"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Activity size={14} className="text-sky-600 shrink-0" />
          <div className="text-[12px] font-black text-[var(--app-text)] uppercase italic">
            Performance History
          </div>
        </div>
        <QualityBadge quality={quality} testid="account-history-perf-quality" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        <SummaryTile
          label="Points"
          value={String(summary.points_count ?? 0)}
          testid="perf-summary-points"
        />
        <SummaryTile
          label="First Point"
          value={fmtEtTime(summary.first_timestamp)}
          testid="perf-summary-first"
        />
        <SummaryTile
          label="Last Point"
          value={fmtEtTime(summary.last_timestamp)}
          testid="perf-summary-last"
        />
        <SummaryTile
          label="Realized P/L"
          value={summary.realized_pnl_total === null || summary.realized_pnl_total === undefined
            ? '—'
            : fmtCurrency(summary.realized_pnl_total)}
          accent={
            summary.realized_pnl_total === null || summary.realized_pnl_total === undefined
              ? 'neutral'
              : summary.realized_pnl_total >= 0
              ? 'pos'
              : 'neg'
          }
          testid="perf-summary-realized"
        />
        <SummaryTile
          label="Unrealized (latest)"
          value={summary.unrealized_pnl_latest === null || summary.unrealized_pnl_latest === undefined
            ? '—'
            : fmtCurrency(summary.unrealized_pnl_latest)}
          accent={
            summary.unrealized_pnl_latest === null || summary.unrealized_pnl_latest === undefined
              ? 'neutral'
              : summary.unrealized_pnl_latest >= 0
              ? 'pos'
              : 'neg'
          }
          testid="perf-summary-unrealized"
        />
      </div>

      {isEmpty ? (
        <div
          data-testid="perf-empty-state"
          className="rounded-lg border border-dashed border-[var(--card-border)] p-3 text-[11px] font-bold text-[var(--muted-text)] italic"
        >
          No realized P/L history has been recorded yet.
        </div>
      ) : (
        <div className="space-y-1" data-testid="perf-points-list">
          {(perf?.points ?? []).slice(-8).reverse().map((pt, idx) => (
            <div
              key={`${pt.timestamp}-${idx}`}
              className="flex items-center justify-between gap-3 rounded-md border border-[var(--card-border)]/60 bg-[var(--card-bg)]/60 px-3 py-2 text-[11px]"
            >
              <div className="flex items-center gap-2 min-w-0">
                {(pt.realized_pnl ?? 0) >= 0 ? (
                  <TrendingUp size={12} className="text-emerald-600 shrink-0" />
                ) : (
                  <TrendingDown size={12} className="text-rose-600 shrink-0" />
                )}
                <div className="min-w-0">
                  <div className="font-black text-[var(--app-text)] truncate">
                    {fmtEtTime(pt.timestamp)}
                  </div>
                  <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest truncate">
                    {pt.strategy_id ? titleize(pt.strategy_id) : 'Snapshot'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="text-right">
                  <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
                    Realized
                  </div>
                  <div
                    className={`font-black ${
                      (pt.realized_pnl ?? 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'
                    }`}
                  >
                    {pt.realized_pnl === null || pt.realized_pnl === undefined
                      ? '—'
                      : fmtCurrency(pt.realized_pnl)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
                    Equity
                  </div>
                  <div className="font-black text-[var(--app-text)]">
                    {pt.equity === null || pt.equity === undefined
                      ? '—'
                      : fmtCurrency(pt.equity)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface SummaryTileProps {
  label: string;
  value: string;
  accent?: 'pos' | 'neg' | 'neutral';
  testid?: string;
}

function SummaryTile({ label, value, accent = 'neutral', testid }: SummaryTileProps) {
  const valueClass =
    accent === 'pos'
      ? 'text-emerald-600'
      : accent === 'neg'
      ? 'text-rose-600'
      : 'text-[var(--app-text)]';
  return (
    <div
      data-testid={testid}
      className="rounded-md border border-[var(--card-border)] bg-[var(--card-bg)]/60 px-2 py-1.5"
    >
      <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
        {label}
      </div>
      <div className={`text-[11px] font-black italic ${valueClass}`}>{value}</div>
    </div>
  );
}

interface LockoutSubsectionProps {
  bundle: ReturnType<typeof useAccountHistory>['bundle'];
}

function LockoutSubsection({ bundle }: LockoutSubsectionProps) {
  const lock = bundle?.lockouts ?? null;
  const quality = bundle?.lockoutsQuality ?? 'UNAVAILABLE';

  const events: LockoutEvent[] = useMemo(() => {
    const list = (lock?.events ?? []).slice();
    list.sort((a, b) => {
      const ta = a?.timestamp ? Date.parse(a.timestamp) : 0;
      const tb = b?.timestamp ? Date.parse(b.timestamp) : 0;
      return tb - ta;
    });
    return list;
  }, [lock]);

  if (quality === 'UNAVAILABLE') {
    return (
      <div
        data-testid="account-history-lockout-unavailable"
        className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 flex items-center gap-3"
      >
        <AlertTriangle size={14} className="text-amber-600 shrink-0" />
        <div className="text-[11px] font-bold text-amber-700">
          Account history is not connected yet.
        </div>
      </div>
    );
  }

  const summary = lock?.summary ?? {
    events_count: 0,
    active_lockouts: 0,
    last_lockout_at: null,
    most_common_reason: null,
  };

  return (
    <div
      data-testid="account-history-lockouts"
      className="rounded-xl border border-[var(--card-border)] bg-[var(--input-bg)]/40 p-4 space-y-3"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Lock size={14} className="text-amber-600 shrink-0" />
          <div className="text-[12px] font-black text-[var(--app-text)] uppercase italic">
            Lockout History
          </div>
        </div>
        <QualityBadge quality={quality} testid="account-history-lockouts-quality" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <SummaryTile
          label="Events"
          value={String(summary.events_count ?? 0)}
          testid="lockout-summary-events"
        />
        <SummaryTile
          label="Active"
          value={String(summary.active_lockouts ?? 0)}
          accent={(summary.active_lockouts ?? 0) > 0 ? 'neg' : 'pos'}
          testid="lockout-summary-active"
        />
        <SummaryTile
          label="Last Lockout"
          value={fmtRelative(summary.last_lockout_at)}
          testid="lockout-summary-last"
        />
        <SummaryTile
          label="Most Common"
          value={humanizeLockoutReason(summary.most_common_reason)}
          testid="lockout-summary-reason"
        />
      </div>

      {events.length === 0 ? (
        <div
          data-testid="lockout-empty-state"
          className="rounded-lg border border-dashed border-[var(--card-border)] p-3 text-[11px] font-bold text-[var(--muted-text)] italic"
        >
          No lockout or rejection events have been recorded for this account.
        </div>
      ) : (
        <div className="space-y-1" data-testid="lockout-events-list">
          {events.slice(0, 12).map((evt, idx) => {
            const sev = lockoutSeverity(evt.severity);
            return (
              <div
                key={`${evt.timestamp}-${idx}`}
                data-testid="lockout-event-row"
                className="flex items-start justify-between gap-3 rounded-md border border-[var(--card-border)]/60 bg-[var(--card-bg)]/60 px-3 py-2"
              >
                <div className="flex items-start gap-2 min-w-0">
                  <ShieldAlert size={12} className="text-amber-600 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[11px] font-black text-[var(--app-text)] truncate">
                      {humanizeLockoutReason(evt.reason)}
                    </div>
                    <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
                      <Clock size={9} className="inline -mt-0.5 mr-0.5" />
                      {fmtEtTime(evt.timestamp)}
                      {evt.strategy_id ? <span> · {titleize(evt.strategy_id)}</span> : null}
                      {evt.lockout_type && evt.lockout_type !== 'unknown' ? (
                        <span> · {titleize(evt.lockout_type)}</span>
                      ) : null}
                    </div>
                    {evt.released_at ? (
                      <div className="text-[9px] font-bold text-emerald-600 uppercase tracking-widest mt-0.5">
                        Released {fmtRelative(evt.released_at)}
                      </div>
                    ) : evt.duration_s !== null && evt.duration_s !== undefined ? (
                      <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest mt-0.5">
                        Duration {Math.round(evt.duration_s)}s
                      </div>
                    ) : null}
                  </div>
                </div>
                <span
                  className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md border shrink-0 ${severityToClass(
                    sev,
                  )}`}
                >
                  {sev === 'unknown' ? titleize(evt.severity) || 'Info' : severityToLabel(sev)}
                </span>
              </div>
            );
          })}
          {events.length > 12 ? (
            <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest italic">
              + {events.length - 12} earlier events (collapsed)
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export interface AccountHistoryPanelProps {
  accountId: string | null;
  accountLabel?: string | null;
}

export default function AccountHistoryPanel({
  accountId,
  accountLabel,
}: AccountHistoryPanelProps) {
  const { bundle, loading, error, refetch } = useAccountHistory(accountId);
  const [diagOpen, setDiagOpen] = useState(false);

  if (!accountId) {
    return (
      <div
        data-testid="account-history-no-account"
        className="rounded-2xl border border-[var(--card-border)] bg-[var(--input-bg)]/40 p-4 text-[11px] font-bold text-[var(--muted-text)] italic"
      >
        Select an account to view performance and lockout history.
      </div>
    );
  }

  if (loading && !bundle) {
    return (
      <div
        data-testid="account-history-loading"
        className="rounded-2xl border border-[var(--card-border)] bg-[var(--input-bg)]/40 p-4 flex items-center gap-3"
      >
        <Loader2 size={14} className="animate-spin text-sky-600" />
        <span className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
          Loading account history…
        </span>
      </div>
    );
  }

  const label =
    bundle?.performance?.account_label ||
    bundle?.lockouts?.account_label ||
    accountLabel ||
    accountId;

  return (
    <div
      data-testid="account-history-panel"
      data-account-id={accountId}
      className="rounded-2xl border border-[var(--card-border)] bg-[var(--input-bg)]/30 p-4 space-y-3"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Database size={14} className="text-sky-600 shrink-0" />
          <div className="min-w-0">
            <div className="text-[12px] font-black text-[var(--app-text)] uppercase italic truncate">
              Account History · {label}
            </div>
            <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
              Last refreshed {fmtRelative(bundle?.fetchedAt)}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {error ? (
            <span className="text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-md border border-rose-500/40 text-rose-600 bg-rose-500/10">
              Endpoint Issue
            </span>
          ) : (
            <span className="text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-md border border-emerald-500/40 text-emerald-600 bg-emerald-500/10">
              <CheckCircle2 size={9} className="inline -mt-0.5 mr-0.5" />
              Read-Only
            </span>
          )}
          <button
            type="button"
            data-testid="account-history-refresh"
            onClick={refetch}
            className="text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-md border border-[var(--card-border)] hover:bg-[var(--card-border)]/30"
          >
            Refresh
          </button>
        </div>
      </div>

      <PerformanceSubsection bundle={bundle} />
      <LockoutSubsection bundle={bundle} />

      <div className="border-t border-[var(--card-border)]/60 pt-2">
        <button
          type="button"
          data-testid="account-history-diagnostics-toggle"
          onClick={() => setDiagOpen((v) => !v)}
          className="flex items-center gap-1.5 text-[9px] font-black text-[var(--muted-text)] uppercase tracking-widest hover:text-[var(--app-text)]"
        >
          {diagOpen ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
          Diagnostics
        </button>
        {diagOpen ? (
          <div
            data-testid="account-history-diagnostics"
            className="mt-2 rounded-md border border-[var(--card-border)]/40 bg-[var(--card-bg)]/40 p-2 text-[10px] font-mono whitespace-pre-wrap break-all text-[var(--muted-text)] max-h-64 overflow-auto"
          >
            {`account_id: ${accountId}\n`}
            {`performance_quality: ${bundle?.performanceQuality ?? 'UNAVAILABLE'}\n`}
            {`lockouts_quality: ${bundle?.lockoutsQuality ?? 'UNAVAILABLE'}\n`}
            {`performance_points: ${bundle?.performance?.points?.length ?? 0}\n`}
            {`lockout_events: ${bundle?.lockouts?.events?.length ?? 0}\n`}
            {bundle?.performance?.generated_at
              ? `performance_generated_at_iso: ${bundle.performance.generated_at}\n`
              : ''}
            {bundle?.lockouts?.generated_at
              ? `lockouts_generated_at_iso: ${bundle.lockouts.generated_at}\n`
              : ''}
          </div>
        ) : null}
      </div>
    </div>
  );
}
